// Mercapoli - City Selector Component
import React, { useState, useEffect } from 'react';
import { MapPin, ChevronDown, Check, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useCity } from '../context/CityContext';
import { config } from '../lib/api';

const CitySelector = ({ variant = 'default', className = '' }) => {
  const { 
    ciudad, 
    ciudades, 
    pais, 
    setPais,
    setCiudad, 
    getCiudadesByPais,
    needsConfirmation 
  } = useCity();
  
  const [paises, setPaises] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingCity, setPendingCity] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadPaises();
  }, []);

  const loadPaises = async () => {
    try {
      const data = await config.getPaises();
      setPaises(data.paises || []);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const handleCitySelect = (nuevaCiudad) => {
    // Si hay items en carrito y es otra ciudad, mostrar confirmación
    if (needsConfirmation && ciudad?.ciudad_id !== nuevaCiudad.ciudad_id) {
      setPendingCity(nuevaCiudad);
      setShowConfirmDialog(true);
    } else {
      setCiudad(nuevaCiudad, true);
    }
    setIsOpen(false);
  };

  const confirmCityChange = () => {
    if (pendingCity) {
      setCiudad(pendingCity, true);
      setPendingCity(null);
    }
    setShowConfirmDialog(false);
  };

  const cancelCityChange = () => {
    setPendingCity(null);
    setShowConfirmDialog(false);
  };

  // Agrupar ciudades por país
  const ciudadesPorPais = paises.reduce((acc, p) => {
    acc[p.codigo] = getCiudadesByPais(p.codigo);
    return acc;
  }, {});

  // Si no hay ciudad seleccionada, mostrar selector más prominente
  if (!ciudad) {
    return (
      <>
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className={`flex items-center gap-2 ${className}`}
              data-testid="city-selector-btn"
            >
              <MapPin className="h-4 w-4 text-primary" />
              <span>Seleccionar ubicación</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-y-auto">
            <DropdownMenuLabel>Selecciona tu ciudad</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {paises.map((p) => (
              <React.Fragment key={p.codigo}>
                {ciudadesPorPais[p.codigo]?.length > 0 && (
                  <>
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                      {p.nombre}
                    </DropdownMenuLabel>
                    {ciudadesPorPais[p.codigo].map((c) => (
                      <DropdownMenuItem
                        key={c.ciudad_id}
                        onClick={() => handleCitySelect(c)}
                        className="cursor-pointer"
                        data-testid={`city-option-${c.ciudad_id}`}
                      >
                        <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                        {c.nombre}
                        <span className="ml-auto text-xs text-muted-foreground">
                          {c.simbolo_moneda}
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </React.Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  }

  // Ciudad seleccionada - mostrar nombre con opción de cambiar
  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button 
            className={`flex items-center gap-1.5 text-sm hover:text-primary transition-colors ${className}`}
            data-testid="city-selector-btn"
          >
            <MapPin className="h-4 w-4 text-primary" />
            <span className="max-w-32 truncate">{ciudad.nombre}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-y-auto">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Cambiar ubicación</span>
            <span className="text-xs font-normal text-muted-foreground">
              {ciudad.moneda}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {paises.map((p) => (
            <React.Fragment key={p.codigo}>
              {ciudadesPorPais[p.codigo]?.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                    {p.nombre}
                  </DropdownMenuLabel>
                  {ciudadesPorPais[p.codigo].map((c) => (
                    <DropdownMenuItem
                      key={c.ciudad_id}
                      onClick={() => handleCitySelect(c)}
                      className="cursor-pointer"
                      data-testid={`city-option-${c.ciudad_id}`}
                    >
                      <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                      {c.nombre}
                      {c.ciudad_id === ciudad.ciudad_id && (
                        <Check className="h-4 w-4 ml-auto text-primary" />
                      )}
                      {c.ciudad_id !== ciudad.ciudad_id && (
                        <span className="ml-auto text-xs text-muted-foreground">
                          {c.simbolo_moneda}
                        </span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog de confirmación al cambiar ciudad con carrito lleno */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Cambiar de ciudad
            </DialogTitle>
            <DialogDescription>
              Al cambiar de ciudad se vaciará tu carrito actual porque los comercios y precios pueden variar.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Ciudad actual</p>
                <p className="font-medium">{ciudad?.nombre}</p>
              </div>
              <div className="text-2xl">→</div>
              <div>
                <p className="text-sm text-muted-foreground">Nueva ciudad</p>
                <p className="font-medium">{pendingCity?.nombre}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelCityChange}>
              Cancelar
            </Button>
            <Button onClick={confirmCityChange} data-testid="confirm-city-change-btn">
              Cambiar y vaciar carrito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CitySelector;
